// ── ImportRosterFlow.js ──────────────────────────────────────────────
import React, { useState, useCallback, useRef, useEffect } from 'react';
import CustomWrestlerEditor from './CustomWrestlerEditor';
import { collection, getDocs, setDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';

// ── Tables de décodage WWE 2K26 → site ──────────────────────────────
const FIGHT_CLASS_MAP = { 0: 'Specialist', 1: 'Fighter', 2: 'Bruiser', 3: 'Cruiser', 4: 'Giant' };
const GENDER_MAP      = { 1: 'M', 2: 'F' };
const STATUS_MAP      = { 0: 'active', 1: 'contract', 2: 'local', 4: 'inactive', 5: 'active' };
const STATUS_LABELS   = { active: 'Active', contract: 'Contract', local: 'Local', inactive: 'Inactive' };
const STATUS_COLORS   = { active: '#2ecc71', contract: '#3498db', local: '#e67e22', inactive: '#e74c3c' };


// ── Déduplication par nom — priorité active > contract > local > inactive ──
const STATUS_PRIORITY = { active: 0, contract: 1, local: 2, inactive: 3, gm: 4 };

function deduplicateWrestlers(wrestlers) {
  const byName = {};
  for (const w of wrestlers) {
    const key = w.name.toLowerCase().trim();
    if (!byName[key]) {
      byName[key] = w;
    } else {
      const existing = byName[key];
      const existingPrio = STATUS_PRIORITY[existing.status] ?? 99;
      const newPrio = STATUS_PRIORITY[w.status] ?? 99;
      // Garder le meilleur status, à égalité le plus haut en popularité
      if (newPrio < existingPrio || (newPrio === existingPrio && w.popularity > existing.popularity)) {
        byName[key] = w;
      }
    }
  }
  return Object.values(byName);
}

function mapGameWrestler(gw) {
  const fight_class = typeof gw.fight_class === 'number' ? (FIGHT_CLASS_MAP[gw.fight_class] || 'Fighter') : (gw.fight_class || 'Fighter');
  const gender = gw.unk_14 === 1 ? 'F' : (typeof gw.gender === 'number' ? (GENDER_MAP[gw.gender] || 'M') : (gw.gender || 'M'));
  const statusNum = typeof gw.status === 'number' ? gw.status : 0;
  const status = STATUS_MAP[statusNum] || 'active';
  const heel = gw.role === 1 || gw.heel === true;
  const nick_name   = gw.nick_name   || '';
  const first_name  = gw.first_name  || '';
  const middle_name = gw.middle_name || '';
  const last_name   = gw.last_name   || '';
  const name = [nick_name, first_name, middle_name, last_name].filter(Boolean).join(' ') || `Wrestler ${gw.wrestler_id}`;
  return {
    wrestler_id: gw.wrestler_id, name, nick_name, first_name, middle_name, last_name,
    status, starting_xp: gw.starting_xp ?? 10, popularity: gw.popularity ?? 50,
    fight_class, location_sdb: gw.location_sdb || 0, gender,
    promo_skill: gw.promo_skill ?? 2, price_value: gw.price_value ?? 0,
    team_name: gw.team_name || '', heel, isOfficial: 'imported', render: '',
  };
}

// ── Firestore helpers ────────────────────────────────────────────────
export async function loadImportedWrestlers(userId) {
  if (!userId) return [];
  try {
    const snap = await getDocs(collection(db, 'users', userId, 'importedWrestlers'));
    return snap.docs.map(d => ({ ...d.data(), _firestoreId: d.id }));
  } catch { return []; }
}

export async function saveImportedWrestlers(userId, wrestlers) {
  if (!userId) return;
  try {
    for (const w of wrestlers) {
      const id = String(w.wrestler_id);
      await setDoc(doc(db, 'users', userId, 'importedWrestlers', id), {
        ...w, _firestoreId: undefined,
      });
    }
  } catch(e) { console.error('saveImportedWrestlers error:', e); }
}

export async function clearImportedWrestlers(userId) {
  if (!userId) return;
  try {
    const snap = await getDocs(collection(db, 'users', userId, 'importedWrestlers'));
    for (const d of snap.docs) await deleteDoc(d.ref);
  } catch(e) { console.error('clearImportedWrestlers error:', e); }
}

// ── Styles ───────────────────────────────────────────────────────────
const S = {
  overlay: { position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Monda', sans-serif" },
  modal: { background: 'rgba(8,8,8,0.98)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px', width: 'min(96vw, 880px)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 80px rgba(0,0,0,0.8)' },
  topBar: { height: '3px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)', flexShrink: 0 },
  header: { padding: '16px 24px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 },
  title: { fontFamily: "'Anton', sans-serif", fontSize: '1rem', letterSpacing: '3px', color: '#fff' },
  subtitle: { fontSize: '0.42rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px', marginTop: '2px' },
  body: { flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '16px' },
  footer: { padding: '14px 24px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 },
  btn: { fontFamily: "'Monda', sans-serif", fontSize: '0.48rem', letterSpacing: '1px', background: 'transparent', border: '1px solid rgba(255,255,255,0.25)', color: '#fff', padding: '8px 18px', borderRadius: '5px', cursor: 'pointer' },
  btnGold: { fontFamily: "'Monda', sans-serif", fontSize: '0.48rem', letterSpacing: '1px', background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.5)', color: '#d4af37', padding: '8px 18px', borderRadius: '5px', cursor: 'pointer' },
  btnDisabled: { opacity: 0.3, cursor: 'not-allowed' },
  label: { fontSize: '0.4rem', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.3)', marginBottom: '6px' },
  pill: (active, color = '#3498db') => ({ fontFamily: "'Monda', sans-serif", fontSize: '0.4rem', letterSpacing: '1px', padding: '4px 10px', borderRadius: '3px', cursor: 'pointer', background: active ? `${color}20` : 'rgba(255,255,255,0.03)', border: `1px solid ${active ? color : 'rgba(255,255,255,0.12)'}`, color: active ? color : 'rgba(255,255,255,0.4)', transition: 'background 0.15s, border-color 0.15s, color 0.15s' }),
  input: { fontFamily: "'Monda', sans-serif", fontSize: '0.52rem', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '5px', color: '#fff', padding: '8px 12px', outline: 'none', width: '100%' },
  toggle: (active) => ({ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.42rem', color: active ? '#d4af37' : 'rgba(255,255,255,0.3)', letterSpacing: '1px', userSelect: 'none' }),
  toggleBox: (active) => ({ width: '32px', height: '16px', borderRadius: '8px', background: active ? 'rgba(212,175,55,0.3)' : 'rgba(255,255,255,0.08)', border: `1px solid ${active ? 'rgba(212,175,55,0.6)' : 'rgba(255,255,255,0.15)'}`, position: 'relative', transition: 'background 0.2s, border-color 0.2s', flexShrink: 0 }),
  toggleDot: (active) => ({ position: 'absolute', top: '2px', left: active ? '16px' : '2px', width: '10px', height: '10px', borderRadius: '50%', background: active ? '#d4af37' : 'rgba(255,255,255,0.3)', transition: 'left 0.2s, background 0.2s' }),
};

// ── Step 1 — Upload ──────────────────────────────────────────────────
function StepUpload({ onParsed, onClose, existingCount, onUseExisting }) {
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef();

  const processFile = useCallback((file) => {
    if (!file) return;
    if (!file.name.endsWith('.json')) { setError('JSON file required (.json)'); return; }
    setLoading(true); setError('');
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        const arr = Array.isArray(data) ? data : (data.wrestlers || data.stars || Object.values(data));
        if (!arr || !arr.length) { setError('No wrestlers found in this file — make sure this is a valid stars.json file.'); setLoading(false); return; }
        if (typeof arr[0] !== 'object') { setError('Invalid format — could not find wrestlers.'); setLoading(false); return; }
        // Validate structure: at least one wrestler must have wrestler_id or a name field
        var hasValidEntry = arr.some(function(w) {
          return w && (w.wrestler_id || w.first_name || w.last_name || w.nick_name);
        });
        if (!hasValidEntry) { setError('No wrestlers found in this file — make sure this is a valid stars.json file.'); setLoading(false); return; }
        onParsed(arr.map(mapGameWrestler));
      } catch { setError('Invalid or corrupted JSON file'); setLoading(false); }
    };
    reader.readAsText(file);
  }, [onParsed]);

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>
        <div style={S.topBar} />
        <div style={S.header}>
          <div><div style={S.title}>⇪ IMPORT ROSTER</div><div style={S.subtitle}>IMPORT FROM STARS.JSON</div></div>
          <button style={{ ...S.btn, padding: '4px 10px' }} onClick={onClose}>✕</button>
        </div>

        {/* Existing imports banner */}
        {existingCount > 0 && (
          <div style={{ padding: '12px 24px', background: 'rgba(212,175,55,0.06)', borderBottom: '1px solid rgba(212,175,55,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <div style={{ fontSize: '0.42rem', color: 'rgba(255,255,255,0.5)' }}>
              ✦ You have <span style={{ color: '#d4af37' }}>{existingCount} previously imported wrestlers</span> saved.
            </div>
            <button style={S.btnGold} onClick={onUseExisting}>USE EXISTING</button>
          </div>
        )}

        <div style={{ ...S.body, alignItems: 'center', justifyContent: 'center', minHeight: '280px' }}>
          <div
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={e => { e.preventDefault(); setDragging(false); processFile(e.dataTransfer.files[0]); }}
            onClick={() => inputRef.current?.click()}
            style={{ width: '100%', maxWidth: '480px', border: `2px dashed ${dragging ? '#d4af37' : 'rgba(255,255,255,0.12)'}`, borderRadius: '12px', padding: '40px 32px', textAlign: 'center', cursor: 'pointer', background: dragging ? 'rgba(212,175,55,0.05)' : 'rgba(255,255,255,0.02)', transition: 'background 0.2s, border-color 0.2s' }}>
            <input ref={inputRef} type="file" accept=".json" style={{ display: 'none' }} onChange={e => processFile(e.target.files[0])} />
            <div style={{ fontSize: '2.5rem', marginBottom: '12px', opacity: 0.6 }}>📁</div>
            <div style={{ fontFamily: "'Anton', sans-serif", fontSize: '0.8rem', letterSpacing: '2px', color: '#fff', marginBottom: '8px' }}>{loading ? 'LOADING...' : existingCount > 0 ? 'IMPORT NEW STARS.JSON' : 'DROP STARS.JSON HERE'}</div>
            <div style={{ fontSize: '0.42rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '1px' }}>or click to select</div>
          </div>
          {error && <div style={{ fontSize: '0.46rem', color: '#e74c3c', marginTop: '8px' }}>⚠ {error}</div>}
          <div style={{ marginTop: '20px', fontSize: '0.4rem', color: 'rgba(255,255,255,0.2)', textAlign: 'center', lineHeight: '1.8', maxWidth: '400px' }}>
            The stars.json file is located in the WWE 2K26 installation directory.<br />No data is sent to an external server.
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Step 2 — Filtres + Sélection ─────────────────────────────────────
function StepFilter({ wrestlers, officialIds, officialNames, onConfirm, onBack }) {
  const [newOnly, setNewOnly]   = useState(true); // NEW ONLY toggle
  const [statusFilters, setStatusFilters] = useState({ active: true, contract: true, local: true, inactive: true });
  const [genderFilters, setGenderFilters] = useState({ M: true, F: true });
  const [manualDesel, setManualDesel] = useState(new Set());
  const [search, setSearch] = useState('');

  const isNew = useCallback((w) => {
    // Vérifier par ID d'abord
    if (officialIds.has(String(w.wrestler_id))) return false;
    // Vérifier par nom ensuite (cas où l'ID diffère mais le nom est identique)
    if (officialNames.has(w.name.toLowerCase().trim())) return false;
    return true;
  }, [officialIds, officialNames]);

  const passesFilter = useCallback((w) =>
    statusFilters[w.status] &&
    genderFilters[w.gender] &&
    (!newOnly || isNew(w)),
  [statusFilters, genderFilters, newOnly, isNew]);

  const isSelected = useCallback((w) =>
    passesFilter(w) && !manualDesel.has(w.wrestler_id),
  [passesFilter, manualDesel]);

  const filtered = wrestlers.filter(w =>
    passesFilter(w) && (!search || w.name.toLowerCase().includes(search.toLowerCase()))
  );

  const selectedWrestlers = wrestlers.filter(isSelected);
  const selectedCount = selectedWrestlers.length;
  const knownCount = selectedWrestlers.filter(w => !isNew(w)).length;
  const newCount = selectedCount - knownCount;

  const toggleAll = () => {
    const allSel = filtered.every(w => isSelected(w));
    setManualDesel(p => {
      const n = new Set(p);
      filtered.forEach(w => allSel ? n.add(w.wrestler_id) : n.delete(w.wrestler_id));
      return n;
    });
  };

  const toggle = (w) => {
    if (!passesFilter(w)) return;
    setManualDesel(p => { const n = new Set(p); n.has(w.wrestler_id) ? n.delete(w.wrestler_id) : n.add(w.wrestler_id); return n; });
  };

  const handleConfirm = () => {
    const result = wrestlers.filter(isSelected).map(w => ({
      ...w,
      _isKnown: !isNew(w),
      isOfficial: !isNew(w) ? true : 'imported',
    }));
    onConfirm(result);
  };

  return (
    <div style={S.overlay}>
      <div style={S.modal}>
        <div style={S.topBar} />
        <div style={S.header}>
          <div><div style={S.title}>⇪ IMPORT ROSTER</div><div style={S.subtitle}>{wrestlers.length} WRESTLERS DETECTED · {wrestlers.filter(isNew).length} NEW</div></div>
          <div style={{ fontSize: '0.42rem', color: 'rgba(255,255,255,0.3)', textAlign: 'right' }}>
            <span style={{ color: '#2ecc71' }}>{knownCount > 0 ? knownCount : ''}</span>{knownCount > 0 ? ' recognized · ' : ''}
            <span style={{ color: '#d4af37' }}>{newCount}</span> new ·&nbsp;
            <span style={{ color: '#fff' }}>{selectedCount}</span> selected
          </div>
        </div>

        {/* Filters bar */}
        <div style={{ padding: '10px 24px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0, display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* NEW ONLY toggle */}
          <div style={S.toggle(newOnly)} onClick={() => setNewOnly(p => !p)}>
            <div style={S.toggleBox(newOnly)}><div style={S.toggleDot(newOnly)} /></div>
            NEW ONLY
          </div>
          <div style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.08)' }} />
          {/* Status */}
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <div style={{ ...S.label, marginBottom: 0, marginRight: '4px' }}>STATUS</div>
            {Object.keys(statusFilters).map(s => (
              <button key={s} style={S.pill(statusFilters[s], STATUS_COLORS[s])} onClick={() => setStatusFilters(p => ({ ...p, [s]: !p[s] }))}>
                {STATUS_LABELS[s].toUpperCase()}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <div style={{ ...S.label, marginBottom: 0, marginRight: '4px' }}>GENDER</div>
            <button style={S.pill(genderFilters.M, '#3498db')} onClick={() => setGenderFilters(p => ({ ...p, M: !p.M }))}>♂ MALE</button>
            <button style={S.pill(genderFilters.F, '#e91e8c')} onClick={() => setGenderFilters(p => ({ ...p, F: !p.F }))}>♀ FEMALE</button>
          </div>
        </div>

        {/* Search + Select All */}
        <div style={{ padding: '8px 24px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0, display: 'flex', gap: '10px', alignItems: 'center' }}>
          <input style={{ ...S.input, flex: 1 }} placeholder="Search a wrestler..." value={search} onChange={e => setSearch(e.target.value)} />
          <button style={S.pill(true, '#fff')} onClick={toggleAll}>
            {filtered.length > 0 && filtered.every(w => isSelected(w)) ? 'DESELECT ALL' : 'SELECT ALL'}
          </button>
        </div>

        {/* Liste */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '6px 24px' }}>
          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px', color: 'rgba(255,255,255,0.2)', fontSize: '0.5rem' }}>No wrestlers match the filters</div>
          ) : filtered.map(w => {
            const sel = isSelected(w);
            const known = !isNew(w);
            return (
              <div key={w.wrestler_id} onClick={() => toggle(w)}
                style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '7px 10px', borderRadius: '5px', cursor: 'pointer', background: sel ? 'rgba(255,255,255,0.03)' : 'transparent', borderBottom: '1px solid rgba(255,255,255,0.04)', opacity: sel ? 1 : 0.4 }}>
                <div style={{ width: '16px', height: '16px', flexShrink: 0, border: `1px solid ${sel ? '#d4af37' : 'rgba(255,255,255,0.2)'}`, borderRadius: '3px', background: sel ? 'rgba(212,175,55,0.2)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.5rem', color: '#d4af37' }}>
                  {sel ? '✓' : ''}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "'Anton', sans-serif", fontSize: '0.6rem', color: sel ? '#fff' : 'rgba(255,255,255,0.5)', letterSpacing: '0.5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{w.name}</div>
                  <div style={{ fontSize: '0.38rem', color: 'rgba(255,255,255,0.25)', letterSpacing: '1px', marginTop: '1px' }}>{w.fight_class} · {w.heel ? 'HEEL' : 'FACE'} · {w.gender === 'F' ? '♀' : '♂'} · Pop {w.popularity}</div>
                </div>
                <div style={{ fontSize: '0.36rem', letterSpacing: '1px', padding: '2px 7px', borderRadius: '3px', flexShrink: 0, background: known ? 'rgba(46,204,113,0.1)' : 'rgba(212,175,55,0.1)', border: `1px solid ${known ? 'rgba(46,204,113,0.3)' : 'rgba(212,175,55,0.3)'}`, color: known ? '#2ecc71' : '#d4af37' }}>
                  {known ? '✅ RECOGNIZED' : '⚠ NEW'}
                </div>
                <div style={{ fontSize: '0.36rem', letterSpacing: '1px', padding: '2px 7px', borderRadius: '3px', flexShrink: 0, background: `${STATUS_COLORS[w.status]}15`, border: `1px solid ${STATUS_COLORS[w.status]}40`, color: STATUS_COLORS[w.status] }}>
                  {STATUS_LABELS[w.status]?.toUpperCase()}
                </div>
              </div>
            );
          })}
        </div>

        <div style={S.footer}>
          <button style={S.btn} onClick={onBack}>← BACK</button>
          <button style={{ ...S.btnGold, ...(selectedCount === 0 ? S.btnDisabled : {}) }} onClick={() => selectedCount > 0 && handleConfirm()} disabled={selectedCount === 0}>
            NEXT → ({selectedCount} wrestlers)
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Step 3 — Render Upload + Edit ───────────────────────────────────
function StepRenders({ wrestlers, onConfirm, onBack, userId }) {
  const [localWrestlers, setLocalWrestlers] = useState(wrestlers);
  const [renders, setRenders]   = useState({});
  const [editTarget, setEditTarget] = useState(null);
  const newWrestlers = localWrestlers.filter(w => !w._isKnown);

  const handleFile = (wrestlerId, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => setRenders(p => ({ ...p, [wrestlerId]: e.target.result }));
    reader.readAsDataURL(file);
  };

  const handleSaved = (saved) => {
    // Mettre à jour le wrestler ET son render dans la liste locale
    setLocalWrestlers(prev => prev.map(w =>
      String(w.wrestler_id) === String(saved.wrestler_id) ? { ...w, ...saved } : w
    ));
    // Mettre à jour le render affiché instantanément
    if (saved.render || saved.customRender) {
      setRenders(p => ({ ...p, [saved.wrestler_id]: saved.render || saved.customRender }));
    }
    setEditTarget(null);
  };

  const getInitials = (w) => [w.first_name, w.last_name].filter(Boolean).map(p => p[0]).join('').toUpperCase() || '?';

  return (
    <>
      <div style={S.overlay}>
        <div style={S.modal}>
          <div style={S.topBar} />
          <div style={S.header}>
            <div><div style={S.title}>⇪ IMPORT ROSTER</div><div style={S.subtitle}>WRESTLER RENDERS & INFO</div></div>
            <div style={{ fontSize: '0.42rem', color: 'rgba(255,255,255,0.3)' }}>{newWrestlers.length} new wrestlers · all optional</div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
            {newWrestlers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px', color: 'rgba(255,255,255,0.4)', fontSize: '0.5rem' }}>
                ✅ All wrestlers are recognized — renders will be assigned automatically.
              </div>
            ) : (
              <>
                <div style={{ fontSize: '0.42rem', color: 'rgba(255,255,255,0.3)', marginBottom: '14px', lineHeight: '1.6' }}>
                  The wrestlers below are not in the official database. Upload a render and edit their info if needed — or skip and continue.
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '10px' }}>
                  {newWrestlers.map(w => {
                    const hasRender = renders[w.wrestler_id];
                    return (
                      <div key={w.wrestler_id} style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${hasRender ? 'rgba(212,175,55,0.4)' : 'rgba(255,255,255,0.08)'}`, borderRadius: '8px', overflow: 'hidden', transition: 'border 0.2s' }}>
                        {/* Render preview */}
                        <div style={{ height: '90px', background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
                            {hasRender
                              ? <img loading="lazy" src={hasRender} alt="" style={{ height: '100%', width: '100%', objectFit: 'contain', objectPosition: 'top' }} />
                              : <div style={{ fontFamily: "'Anton', sans-serif", fontSize: '1.6rem', color: 'rgba(255,255,255,0.12)', letterSpacing: '2px' }}>{getInitials(w)}</div>
                            }
                            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', opacity: 0, transition: 'opacity 0.2s', flexDirection: 'column', gap: '4px' }}
                              onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                              onMouseLeave={e => e.currentTarget.style.opacity = '0'}>
                              <div style={{ fontSize: '1.2rem' }}>📷</div>
                              <div style={{ fontSize: '0.36rem', color: 'rgba(255,255,255,0.6)', letterSpacing: '1px' }}>UPLOAD RENDER</div>
                            </div>
                          </div>
                        {/* Infos + bouton edit */}
                        <div style={{ padding: '6px 8px' }}>
                          <div style={{ fontFamily: "'Anton', sans-serif", fontSize: '0.46rem', color: '#fff', letterSpacing: '0.5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{w.name}</div>
                          <div style={{ fontSize: '0.34rem', color: 'rgba(255,255,255,0.25)', marginTop: '1px' }}>{w.fight_class} · {w.heel ? 'HEEL' : 'FACE'}</div>
                          <div style={{ marginTop: '6px' }}>
                            <button
                              onClick={() => setEditTarget(w)}
                              style={{ width: '100%', fontFamily: "'Monda', sans-serif", fontSize: '0.34rem', padding: '4px 0', background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.4)', borderRadius: '3px', cursor: 'pointer', letterSpacing: '0.5px' }}>
                              ✎ EDIT INFO & RENDER
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          <div style={S.footer}>
            <button style={S.btn} onClick={onBack}>← BACK</button>
            <button style={S.btnGold} onClick={() => onConfirm(localWrestlers.map(w => ({ ...w, render: renders[w.wrestler_id] || w.render || '' })))}>NEXT →</button>
          </div>
        </div>
      </div>

      {/* CustomWrestlerEditor — s'ouvre par dessus */}
      {editTarget && (
        <CustomWrestlerEditor
          wrestler={editTarget}
          userId={userId}
          onClose={() => setEditTarget(null)}
          onSaved={(saved) => handleSaved(saved)}
          zIndex={1100}
        />
      )}
    </>
  );
}

// ── Step 4 — Confirmation + Nom ──────────────────────────────────────
function StepConfirm({ wrestlers, onImport, onBack }) {
  const [rosterName, setRosterName] = useState('');
  const knownCount = wrestlers.filter(w => w._isKnown).length;
  const newCount   = wrestlers.length - knownCount;

  return (
    <div style={S.overlay}>
      <div style={{ ...S.modal, maxWidth: '480px' }}>
        <div style={S.topBar} />
        <div style={S.header}>
          <div><div style={S.title}>⇪ IMPORT ROSTER</div><div style={S.subtitle}>CONFIRMATION</div></div>
        </div>
        <div style={{ ...S.body, gap: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
            {[
              { label: 'TOTAL', value: wrestlers.length, color: '#fff' },
              { label: 'RECOGNIZED', value: knownCount, color: '#2ecc71' },
              { label: 'NEW', value: newCount, color: '#d4af37' },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', padding: '14px', textAlign: 'center' }}>
                <div style={{ fontFamily: "'Anton', sans-serif", fontSize: '1.4rem', color, letterSpacing: '1px' }}>{value}</div>
                <div style={{ fontSize: '0.38rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '1.5px', marginTop: '2px' }}>{label}</div>
              </div>
            ))}
          </div>
          <div>
            <div style={S.label}>ROSTER NAME *</div>
            <input style={{ ...S.input, fontFamily: "'Anton', sans-serif", fontSize: '0.75rem', letterSpacing: '1px', padding: '10px 14px' }}
              placeholder="e.g. My WWE 2K26 Roster" value={rosterName}
              onChange={e => setRosterName(e.target.value)} autoFocus />
          </div>
        </div>
        <div style={S.footer}>
          <button style={S.btn} onClick={onBack}>← BACK</button>
          <button style={{ ...S.btnGold, ...(!rosterName.trim() ? S.btnDisabled : {}) }}
            onClick={() => rosterName.trim() && onImport(wrestlers, rosterName.trim())}
            disabled={!rosterName.trim()}>
            ✦ IMPORT
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Composant principal ──────────────────────────────────────────────
export default function ImportRosterFlow({ onClose, onImport, officialWrestlers = [], userId }) {
  const [step, setStep]               = useState('upload');
  const [parsed, setParsed]           = useState([]);
  const [selected, setSelected]       = useState([]);
  const [existingImports, setExisting] = useState([]);
  const [loadingExisting, setLoading]  = useState(true);

  const officialIds   = new Set((officialWrestlers || []).map(w => String(w.wrestler_id)));
  const officialNames = new Set((officialWrestlers || []).map(w => (w.name || '').toLowerCase().trim()).filter(Boolean));

  // Charger les imports existants depuis Firestore
  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    loadImportedWrestlers(userId).then(w => { setExisting(w); setLoading(false); });
  }, [userId]);

  const handleParsed = (wrestlers) => { setParsed(deduplicateWrestlers(wrestlers)); setStep('filter'); };
  const handleFilter = (wrestlers) => { setSelected(wrestlers); setStep('renders'); };
  const handleRenders = (wrestlers) => { setSelected(wrestlers); setStep('confirm'); };

  const handleUseExisting = () => {
    const withKnown = existingImports.map(w => ({ ...w, _isKnown: officialIds.has(String(w.wrestler_id)) }));
    setSelected(withKnown);
    setStep('confirm');
  };

  const handleImport = async (wrestlers, rosterName) => {
    // Sauvegarder dans Firestore
    const toSave = wrestlers.filter(w => !w._isKnown); // sauvegarder seulement les nouveaux
    if (toSave.length > 0) await saveImportedWrestlers(userId, toSave);
    // Retourner le roster propre
    const clean = wrestlers.map(({ _isKnown, ...w }) => w);
    onImport(clean, rosterName);
  };

  if (loadingExisting) return (
    <div style={S.overlay}>
      <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.5rem', letterSpacing: '2px' }}>LOADING...</div>
    </div>
  );

  if (step === 'upload')  return <StepUpload onParsed={handleParsed} onClose={onClose} existingCount={existingImports.length} onUseExisting={handleUseExisting} />;
  if (step === 'filter')  return <StepFilter wrestlers={parsed} officialIds={officialIds} officialNames={officialNames} onConfirm={handleFilter} onBack={() => setStep('upload')} />;
  if (step === 'renders') return <StepRenders wrestlers={selected} onConfirm={handleRenders} onBack={() => setStep('filter')} userId={userId} />;
  if (step === 'confirm') return <StepConfirm wrestlers={selected} onImport={handleImport} onBack={() => setStep('renders')} />;
  return null;
}
